import React, { useState, useRef, useEffect } from "react";
import moment from "moment";
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import "./style.scss";

const DateNavigationHeader = ({ selectedDate, onDateChange }) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const calendarRef = useRef(null);

  const handlePrevDay = () => {
    const prevDate = moment(selectedDate).subtract(1, "days").toDate();
    onDateChange(prevDate);
  };

  const handleNextDay = () => {
    const nextDate = moment(selectedDate).add(1, "days").toDate();
    onDateChange(nextDate);
  };

  const handleDateSelect = (date) => {
    onDateChange(date);
    setShowCalendar(false);
  };

  const formattedDateOnly = moment(selectedDate)
    .locale("en")
    .format("DD-MM-YYYY");

  const formattedDay = moment(selectedDate).locale("en").format("dddd");

  const handleTodayClick = () => {
    onDateChange(moment().toDate());
  };

  const todayDate = moment().locale("en").format("DD");

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };

    if (showCalendar) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCalendar]);

  const generateCalendarDays = () => {
    const startOfMonth = moment(selectedDate).startOf("month");
    const endOfMonth = moment(selectedDate).endOf("month");
    const startDate = startOfMonth.clone().startOf("week");
    const endDate = endOfMonth.clone().endOf("week");

    const days = [];
    let day = startDate.clone();

    while (day.isBefore(endDate, "day")) {
      days.push(day.clone());
      day.add(1, "day");
    }

    return days;
  };

  const calendarDays = generateCalendarDays();
  const currentMonth = moment(selectedDate).locale("en").format("MMMM YYYY");

  return (
    <div className="date-navigation-header">
      <button
        className="today-button"
        onClick={handleTodayClick}
        aria-label="Go to today"
      >
        <div className="calendar-icon-wrapper">
          <svg
            className="calendar-icon-custom"
            viewBox="0 0 24 24"
            width="32"
            height="32"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          <span className="calendar-date-number">{todayDate}</span>
        </div>
      </button>

      <button
        className="nav-arrow"
        onClick={handlePrevDay}
        aria-label="Previous day"
      >
        <ChevronLeft size={22} />
      </button>

      <div className="date-display-wrapper">
        <h5 className="date-display">
          {formattedDateOnly} <span className="day-name">{formattedDay}</span>
        </h5>
        <button
          className="calendar-icon"
          onClick={() => setShowCalendar(!showCalendar)}
          aria-label="Open calendar"
        >
          <Calendar size={24} />
        </button>

        {showCalendar && (
          <div className="calendar-dropdown" ref={calendarRef}>
            <div className="calendar-header">
              <button
                onClick={() =>
                  onDateChange(
                    moment(selectedDate).subtract(1, "month").toDate()
                  )
                }
              >
                <ChevronLeft size={18} />
              </button>
              <span>{currentMonth}</span>
              <button
                onClick={() =>
                  onDateChange(moment(selectedDate).add(1, "month").toDate())
                }
              >
                <ChevronRight size={18} />
              </button>
            </div>

            <div className="calendar-weekdays">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                <div key={day} className="weekday">
                  {day}
                </div>
              ))}
            </div>

            <div className="calendar-days">
              {calendarDays.map((day, index) => {
                const isCurrentMonth =
                  day.month() === moment(selectedDate).month();
                const isSelected = day.isSame(selectedDate, "day");
                const isToday = day.isSame(moment(), "day");

                return (
                  <button
                    key={index}
                    className={`calendar-day ${
                      !isCurrentMonth ? "other-month" : ""
                    } ${isSelected ? "selected" : ""} ${
                      isToday ? "today" : ""
                    }`}
                    onClick={() => handleDateSelect(day.toDate())}
                  >
                    {day.date()}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <button
        className="nav-arrow"
        onClick={handleNextDay}
        aria-label="Next day"
      >
        <ChevronRight size={22} />
      </button>
    </div>
  );
};

export default DateNavigationHeader;
